
import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  Home, 
  Users, 
  ShoppingBag, 
  BarChart2, 
  Settings, 
  LogOut,
  Database,
  Shield,
  ChevronLeft,
  ChevronRight,
  Menu
} from 'lucide-react';
import { useAuth } from '@/contexts/auth-context';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useToast } from '@/components/ui/use-toast';

const AdminSidebar: React.FC = () => {
  const location = useLocation();
  const { logout } = useAuth();
  const { toast } = useToast();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const navItems = [
    { 
      label: "Dashboard", 
      icon: <Home className="h-5 w-5" />, 
      path: "/admin" 
    },
    { 
      label: "Users", 
      icon: <Users className="h-5 w-5" />, 
      path: "/admin/users" 
    },
    { 
      label: "Agents", 
      icon: <ShoppingBag className="h-5 w-5" />, 
      path: "/admin/agents" 
    },
    { 
      label: "Transactions", 
      icon: <Database className="h-5 w-5" />, 
      path: "/admin/transactions" 
    },
    { 
      label: "Analytics", 
      icon: <BarChart2 className="h-5 w-5" />, 
      path: "/admin/analytics" 
    },
    { 
      label: "Settings", 
      icon: <Settings className="h-5 w-5" />, 
      path: "/admin/settings" 
    },
    {
      label: "File Validation",
      icon: <Shield className="h-5 w-5" />,
      path: "/admin/file-validation"
    }
  ];

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const handleLogout = async () => {
    try {
      await logout();
      toast({
        title: "Success",
        description: "You have been logged out",
      });
    } catch (error) {
      console.error('Logout failed:', error);
      toast({
        title: "Error",
        description: "Failed to log out",
        variant: "destructive"
      });
    }
  };

  // Mobile sidebar toggle
  const toggleMobileSidebar = () => {
    setMobileOpen(!mobileOpen);
  };

  // Desktop sidebar expand/collapse
  const toggleSidebar = () => {
    setCollapsed(!collapsed);
  };

  return (
    <>
      {/* Mobile Sidebar Button */}
      <div className="md:hidden fixed top-4 left-4 z-30">
        <Button 
          variant="outline" 
          size="icon"
          onClick={toggleMobileSidebar}
          className="bg-background"
        >
          <Menu className="h-5 w-5" />
        </Button>
      </div>
      
      {/* Sidebar Overlay for Mobile */}
      {mobileOpen && (
        <div 
          className="md:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setMobileOpen(false)}
        />
      )}
      
      {/* Sidebar */}
      <div className={cn(
        "fixed left-0 top-0 bottom-0 z-50 transition-all duration-300 bg-white shadow-lg dark:bg-gray-800 flex flex-col",
        collapsed ? "w-[80px]" : "w-64",
        mobileOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"
      )}>
        {/* Header */}
        <div className={cn(
          "flex items-center justify-between p-4 border-b",
          collapsed ? "justify-center" : ""
        )}>
          {!collapsed && (
            <h1 className="text-xl font-bold text-primary">Admin</h1>
          )}
          {collapsed && (
            <Shield className="h-6 w-6 text-primary" />
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleSidebar}
            className="hidden md:flex"
          >
            {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setMobileOpen(false)}
            className="md:hidden"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
        </div>
        
        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto p-4">
          <div className="space-y-2">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setMobileOpen(false)}
                className={cn(
                  "flex items-center rounded-md p-2 transition-colors",
                  collapsed ? "justify-center" : "",
                  isActive(item.path)
                    ? "bg-primary text-primary-foreground"
                    : "hover:bg-gray-100 dark:hover:bg-gray-700"
                )}
              >
                <div className={collapsed ? "" : "mr-2"}>
                  {item.icon}
                </div>
                {!collapsed && <span>{item.label}</span>}
              </Link>
            ))}
          </div>
        </nav>
        
        {/* Footer */}
        <div className="p-4 border-t">
          <Link 
            to="/" 
            className={cn(
              "flex items-center rounded-md p-2 text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700 mb-2",
              collapsed ? "justify-center" : ""
            )}
            onClick={() => setMobileOpen(false)}
          >
            <Home className={collapsed ? "" : "mr-2"} />
            {!collapsed && <span>Return to Site</span>}
          </Link>
          <Button 
            variant="destructive" 
            className={cn("w-full", collapsed ? "p-2" : "")}
            onClick={handleLogout}
          >
            <LogOut className={collapsed ? "" : "mr-2"} />
            {!collapsed && "Logout"}
          </Button>
        </div>
      </div>
    </>
  );
};

export default AdminSidebar;
